import { toggle } from "@otto-ec/global-resources/toggle";
import { type DataContainer, type Feature, type FeatureStatus, submitMerge } from "@otto-ec/tracking-bct";
import { captureErrorWithPayload } from "utils/error";
import { preventClickBubbling, registerVisibilityObserver } from "utils/events";
import { createExpiryScheduler, type ExpiryScheduler } from "utils/expiry";
import { fetchRetry } from "utils/fetch";
import { createLogger } from "utils/logging";
import { generateRandomString } from "utils/utils";

const attributeNameBenefitId = "benefit-id";
const attributeNameOriginFeature = "feature";
const attributeNameTeam = "team";
const attributeNameVariationId = "variation-id";
const attributeNameTargetContextDreson = "target-context-dreson";
const attributeNameCurrentContextDreson = "current-context-dreson";
const shouldSendTracking = "send-tracking";
const attributeNameTrackingParentId = "tracking-parent-id";
const attributeNameSendEvents = "send-events"; /*                  */
const attributeNameRemoveExpired = "remove-expired";
const loadOnVisibility = "load-on-visibility";
const attributeNameData = "data";

const log = createLogger("tag", "tag");
const BENEFIT_TAG_NAME = "ft9-benefit-tag-v1";

const ERROR_KEY = "ft9-benefit-tag-not-ok";
const PROMO_FEATURE_LABEL = "promo_Feature";

/*                                                                                                    */
/*                                                                              */
const MAX_CLEANUP_PERIOD_IN_MILLIS = 20 * 24 * 60 * 60 * 1000;

export class BenefitTagElement extends HTMLElement {
	expiry: ExpiryScheduler = createExpiryScheduler();

	async connectedCallback() {
		const data = this.getAttribute(attributeNameData);
		if (data) {
			const success = this.renderFromData(data);
			if (success) {
				return;
			}
			log.warn("Failed to render from data attribute, falling back to API call");
		}
		await this.fetchAndRender();
	}

	disconnectedCallback() {
		this.expiry.cleanup();
	}

	private async fetchAndRender() {
		this.replaceChildren(); /*                       */

		if (this.hasAttribute(loadOnVisibility)) {
			registerVisibilityObserver(this, async () => {
				this.removeAttribute(loadOnVisibility);

				await this.fetchAndRender();
			});
			return;
		}

		const targetContextDreson = this.getAttribute(attributeNameTargetContextDreson);
		if (targetContextDreson === "preview-dreson") {
			/*                                                         */
			return;
		}

		const benefitId = this.getAttribute(attributeNameBenefitId);
		const variationId = this.getAttribute(attributeNameVariationId);
		if (!targetContextDreson && !benefitId && !variationId) {
			log.error("No psr, benefitId nor variationId provided, cannot load tag.");
			captureErrorWithPayload("No psr, benefitId nor variationId provided", "tag", "tag", ERROR_KEY);
			return;
		}

		function buildFeatureHeader(tagElement: BenefitTagElement): string | null {
			const team = tagElement.getAttribute(attributeNameTeam)?.replace("-", "");
			const feature = tagElement.getAttribute(attributeNameOriginFeature);
			if (!team || !feature) return null;
			return `${feature}@${team}-benefit-tag`;
		}

		function buildRequestBody(tagElement: BenefitTagElement): TagApiRequestBody | null {
			const currentContextPsr = tagElement.getAttribute(attributeNameCurrentContextDreson) ?? undefined;
			const benefitId = tagElement.getAttribute(attributeNameBenefitId);
			const variationId = tagElement.getAttribute(attributeNameVariationId);
			const targetContextDreson = tagElement.getAttribute(attributeNameTargetContextDreson);

			if (benefitId) {
				return { benefitIds: [benefitId], currentContextPsr: currentContextPsr };
			}
			if (variationId) {
				return { variationIds: [variationId], currentContextPsr: currentContextPsr };
			}
			if (targetContextDreson) {
				return { targetContextPsr: [targetContextDreson], currentContextPsr: currentContextPsr };
			}

			return null;
		}

		const ottoFeatureHeader = buildFeatureHeader(this);
		const requestBody = buildRequestBody(this);
		if (!requestBody || !ottoFeatureHeader) {
			log.error("Missing required attributes for API call (team, feature, and one of benefitId/variationId/targetContextPsr)");
			return;
		}

		log.info("Calling tag API for %s", this.id || "id-not-set");

		try {
			const response = await fetchRetry({
				url: "/benefit-tag/api/tag",
				namespace: "tag",
				fetchOptions: {
					method: "POST",
					headers: {
						Accept: "application/json",
						"Content-Type": "application/json",
						"otto-feature": ottoFeatureHeader,
						"X-Requested-With": "XMLHttpRequest",
					},
					body: JSON.stringify(requestBody),
					priority: "auto",
				},
				retryOptions: {
					limit: 2,
					delay: 50,
				},
			});

			if (!response.ok) {
				log.error("tag API endpoint returns a non ok response!", response.status);
				captureErrorWithPayload("fetchAndRender error", "tag", "tag", ERROR_KEY, response?.status);
				return;
			}

			function extractDataFromResponse(json: Record<string, Record<string, { data: string }>>): string | null {
				const responseMap = json.benefits ?? json.variations ?? json.targetContextPsr;
				if (!responseMap) {
					return null;
				}
				const firstEntry = Object.values(responseMap)[0];
				return firstEntry?.data ?? null;
			}

			const json = await response.json();
			const dataEntry = extractDataFromResponse(json);
			if (dataEntry) {
				this.renderFromData(dataEntry);
			}
		} catch (error) {
			log.error("tag API request returns an error!", error);
			captureErrorWithPayload("fetchAndRender error", "tag", "tag", ERROR_KEY, error);
		}
	}

	private renderFromData(base64Data: string): boolean {
		/*                                                                                                          */
		function decodeBase64AsUtf8(base64: string): string {
			const binaryString = atob(base64);
			const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));
			return new TextDecoder().decode(bytes);
		}

		try {
			this.innerHTML = decodeBase64AsUtf8(base64Data);
			this.initTag();
			preventClickBubbling(this);
			return true;
		} catch (error) {
			log.error("Failed to decode data attribute", error);
			captureErrorWithPayload("data attribute decode error", "tag", "tag", ERROR_KEY, error);
			return false;
		}
	}

	private initTag() {
		const tag = this.firstElementChild;
		if (!(tag && tag instanceof HTMLElement)) {
			return;
		}

		/*                                              */
		if (this.hasAttribute(attributeNameRemoveExpired) && tag.dataset.expirationDate) {
			this.expiry.scheduler(tag.dataset.expirationDate, this.triggerReload.bind(this));
		}

		/*              */
		const trackingParentId = this.getAttribute(attributeNameTrackingParentId);
		this.sendTracking(tag, trackingParentId);
	}

	private sendTracking(tag: HTMLElement, trackingParentId: string | null) {
		if (!this.hasAttribute(shouldSendTracking)) {
			return;
		}

		const trackingData = tag.dataset.trackingLabels;
		if (!trackingData) {
			return;
		}

		let labels = JSON.parse(trackingData) as DataContainer;
		if (trackingParentId) {
			const { [PROMO_FEATURE_LABEL]: _promoFeature, ...remainingLabels } = labels;
			labels = remainingLabels;
		}

		let feature = {
			id: generateRandomString(20),
			name: "Benefit",
			status: "loaded" as FeatureStatus,
			labels,
		} as Feature;

		if (trackingParentId) {
			feature = {
				...feature,
				parentId: trackingParentId,
			};
		}

		submitMerge({}, [feature]);
	}

	async triggerReload() {
		await this.fetchAndRender();
	}
}

interface TagApiRequestBody {
	benefitIds?: string[];
	variationIds?: string[];
	targetContextPsr?: string[];
	currentContextPsr?: string;
	originFeature?: string;
	team?: string;
}

if (customElements && customElements.get(BENEFIT_TAG_NAME) === undefined && !toggle.get("ftnine_emergency_custom_element_disable_tag", false)) {
	customElements.define(BENEFIT_TAG_NAME, BenefitTagElement);
}
